import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  TextField,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox
} from '@mui/material';
import { SectionCard } from './ui';
import { tint } from '../theme';
import { sanitizeEmail } from '../utils/inputSanitizer';
import { licenseService } from '../services/licenseService';
import { LicenseENUM, ToolENUM, LicenseInput } from '../types/license';
import { CreateLicenseForm } from './LicenseManagement/CreateLicenseForm';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

/**
 * Client khong con gui license request; trang nay chi con kich hoat hang loat:
 * dan serial -> Check -> chon -> Activate. Model co dinh (backend van can field
 * model), nen khong con combobox Tool.
 */
const LICENSE_MODEL = ToolENUM.MiChanger;

interface CreateLicenseDialogProps {
  open: boolean;
  onClose: () => void;
  email: string;
  serialNo: string;
  type: LicenseENUM;
  dateStart: number | null;
  dateEnd: number | null;
  tool: ToolENUM;
  durationInDay: number;
  onSuccess: () => void;
}

interface BatchLicense {
  email: string;
  serialNo: string;
  model: string;
  remainingDays: number;
  isNew: boolean;
  dateEnd: number | null;
  dateStart: number | null;
  durationInDay: number | null;
  type: string | null;
}

interface BatchActivateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (licenseInput: LicenseInput) => void;
  selectedCount: number;
  loading?: boolean;
}

const CreateLicenseDialog: React.FC<CreateLicenseDialogProps> = ({
  open,
  onClose,
  email,
  serialNo,
  type,
  dateStart,
  dateEnd,
  tool,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [license, setLicense] = useState<LicenseInput>({
    email,
    serialNo,
    type,
    model: tool,
    durationInDay: 30,
    durationDayCustom: 0,
    dateStart,
    dateEnd
  });
  const [extensionDialog, setExtensionDialog] = useState<{
    open: boolean;
    remainingDays: number;
    license: LicenseInput | null;
  }>({
    open: false,
    remainingDays: 0,
    license: null
  });
  const [extensionLoading, setExtensionLoading] = useState(false);

  // Update license state when props change
  useEffect(() => {
    setLicense({
      email,
      serialNo,
      type,
      model: tool,
      durationInDay: 30,
      durationDayCustom: 0,
      dateStart,
      dateEnd
    });
  }, [email, serialNo, type, tool, dateStart, dateEnd]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await licenseService.createLicense(license);

      if (result.result) {
        onSuccess();
        onClose();
      } else if (result.message === 'CONFIRM_EXTENSION') {
        if (result.remainingDays && typeof result.remainingDays === 'number') {
          setExtensionDialog({
            open: true,
            remainingDays: result.remainingDays,
            license: license
          });
        } else {
          console.error('Invalid extension data structure:', result);
          setError('Invalid extension confirmation data');
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to create license');
      console.error('Error creating license:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmExtension = async () => {
    if (!extensionDialog.license) return;

    try {
      setExtensionLoading(true);
      const response = await licenseService.extendLicense(extensionDialog.license);
      if (response.result) {
        onSuccess();
        setExtensionDialog({ open: false, remainingDays: 0, license: null });
        onClose();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to extend license');
    } finally {
      setExtensionLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New License</DialogTitle>
        <DialogContent>
          <CreateLicenseForm
            license={license}
            onLicenseChange={setLicense}
            disabledFields={{ email: true, serialNo: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create License'}
          </Button>
        </DialogActions>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
      </Dialog>

      {/* Extension Confirmation Dialog */}
      <Dialog
        open={extensionDialog.open}
        onClose={() => setExtensionDialog({ open: false, remainingDays: 0, license: null })}
      >
        <DialogTitle>Confirm License Extension</DialogTitle>
        <DialogContent>
          <Typography>
            Device còn {extensionDialog.remainingDays} ngày active, thực hiện cộng dồn không?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtensionDialog({ open: false, remainingDays: 0, license: null })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmExtension} variant="contained" color="primary" disabled={extensionLoading}>
            {extensionLoading ? 'Confirming...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const BatchActivateDialog: React.FC<BatchActivateDialogProps> = ({
  open,
  onClose,
  onConfirm,
  selectedCount,
  loading
}) => {
  const [licenseInput, setLicenseInput] = useState<LicenseInput>({
    email: '',
    serialNo: '',
    durationInDay: 30,
    durationDayCustom: 0,
    type: LicenseENUM.BASIC,
    dateStart: Date.now(),
    model: LICENSE_MODEL
  });

  const handleChange = (field: keyof LicenseInput, value: any) => {
    console.log('BatchActivateDialog - handleChange:', { field, value });
    setLicenseInput(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Activate {selectedCount} Licenses</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, pt: 2 }}>
            <Box sx={{ display: 'flex' }}>
              <FormControl fullWidth>
                <InputLabel>License Type</InputLabel>
                <Select
                  value={licenseInput.type}
                  label="License Type"
                  onChange={(e) => handleChange('type', e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {Object.values(LicenseENUM).map((type: LicenseENUM) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}></Box>
            {/* <Box sx={{ display: 'flex' }}>
              <DatePicker
                label="Start Date"
                value={licenseInput.dateStart ? new Date(licenseInput.dateStart) : new Date()}
                onChange={(date) => handleChange('dateStart', date ? date.getTime() : Date.now())}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box> */}
            <Box sx={{ display: 'flex' }}>
              <FormControl fullWidth>
                <InputLabel>Duration (Days)</InputLabel>
                <Select
                  value={licenseInput.durationInDay}
                  label="Duration (Days)"
                  onChange={(e) => handleChange('durationInDay', Number(e.target.value))}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {[30, 60, 90, 180, 360].map((days) => (
                    <MenuItem key={days} value={days}>{days}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <TextField
                fullWidth
                type="number"
                label="Custom Duration (Days)"
                variant="outlined"
                value={licenseInput.durationDayCustom === 0 ? '' : licenseInput.durationDayCustom}
                onChange={(e) => {
                  const value = e.target.value;
                  const parsedValue = value === '' ? 0 : parseInt(value);
                  console.log('Custom Duration Input:', { value, parsedValue });
                  handleChange('durationDayCustom', parsedValue);
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              console.log('BatchActivateDialog - onConfirm called with:', licenseInput);
              onConfirm(licenseInput);
            }}
            variant="contained"
            color="primary"
            disabled={!licenseInput.type || loading}
          >
            {loading ? 'Activating...' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

const LicenseRequestList: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [batchSerialNumbers, setBatchSerialNumbers] = useState('');
  const [batchEmail, setBatchEmail] = useState('');
  const [batchLicenses, setBatchLicenses] = useState<BatchLicense[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [selectedBatchLicense, setSelectedBatchLicense] = useState<BatchLicense | null>(null);
  const [selectedBatchLicenses, setSelectedBatchLicenses] = useState<BatchLicense[]>([]);
  const [batchActivateDialog, setBatchActivateDialog] = useState<{
    open: boolean;
    licenseInput: LicenseInput | null;
  }>({
    open: false,
    licenseInput: null
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activatedLicenses, setActivatedLicenses] = useState<Set<string>>(new Set());
  const [batchActivateLoading, setBatchActivateLoading] = useState(false);

  const validateLicense = (license: BatchLicense) => {
    return license.email && license.serialNo && license.email.trim() !== '' && license.serialNo.trim() !== '';
  };

  const getInvalidLicensesMessage = (licenses: BatchLicense[]) => {
    const invalidLicenses = licenses.filter(license => !validateLicense(license));
    if (invalidLicenses.length === 0) return null;

    const invalidDetails = invalidLicenses.map(license =>
      `${license.email || 'No Email'}-${license.serialNo || 'No SerialNo'}`
    ).join(', ');

    return `Licenses with missing information: ${invalidDetails}`;
  };

  const handleBatchCheck = async () => {
    try {
      setBatchLoading(true);
      setBatchError(null);

      // Sanitize email and serial numbers
      const sanitizedEmail = batchEmail ? sanitizeEmail(batchEmail) : undefined;

      // Process and deduplicate serial numbers with sanitization
      const serialNumbers = batchSerialNumbers
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

      if (serialNumbers.length === 0) {
        setBatchError('Please enter at least one serial number');
        return;
      }

      const result = await licenseService.getLicensesBySerialNumbers(serialNumbers, LICENSE_MODEL, sanitizedEmail);
      setBatchLicenses(result.licenses);
      // Kich hoat hang loat la muc dich chinh: chon san moi dong hop le,
      // nguoi dung chi bo chon nhung dong khong muon.
      setSelectedBatchLicenses(result.licenses.filter(validateLicense));
    } catch (err) {
      setBatchError('Failed to fetch license information');
      console.error('Error fetching batch licenses:', err);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchActivate = (license: BatchLicense) => {
    setSelectedBatchLicense(license);
    setBatchDialogOpen(true);
  };

  const handleBatchCreateSuccess = (license: BatchLicense) => {
    setSuccessMessage('License activated successfully');
    setBatchDialogOpen(false);
    setSelectedBatchLicense(null);
    // Add activated licenses to the set
    const newActivatedLicenses = new Set(activatedLicenses);
    newActivatedLicenses.add(`${license.email}-${license.serialNo}`);
    setActivatedLicenses(newActivatedLicenses);

    // Refresh data from DB to get the latest information
    console.log('Refreshing batch license data after individual activation...');
    handleBatchCheck(); // Refresh the list
  };

  const handleSelectBatchLicense = (license: BatchLicense) => {
    // Validate license before allowing selection
    if (!validateLicense(license)) {
      setError(`Cannot select license with missing information: ${license.email || 'No Email'}-${license.serialNo || 'No SerialNo'}`);
      return;
    }

    setSelectedBatchLicenses(prev => {
      const isSelected = prev.some(l => l.email === license.email && l.serialNo === license.serialNo);
      if (isSelected) {
        return prev.filter(l => !(l.email === license.email && l.serialNo === license.serialNo));
      } else {
        return [...prev, license];
      }
    });
  };

  const handleSelectAllBatchLicenses = () => {
    if (selectedBatchLicenses.length === batchLicenses.length) {
      setSelectedBatchLicenses([]);
    } else {
      // Only select valid licenses
      const validLicenses = batchLicenses.filter(validateLicense);

      const invalidMessage = getInvalidLicensesMessage(batchLicenses);
      if (invalidMessage) {
        setError(invalidMessage);
      }

      setSelectedBatchLicenses(validLicenses);
    }
  };

  const handleBatchActivateSelected = async () => {
    // Validate that all selected licenses have required fields
    const invalidMessage = getInvalidLicensesMessage(selectedBatchLicenses);
    if (invalidMessage) {
      setError(invalidMessage);
      return;
    }

    setBatchActivateDialog({
      open: true,
      licenseInput: null
    });
  };

  const handleBatchActivateConfirm = async (licenseInput: LicenseInput) => {
    try {
      setBatchActivateLoading(true);

      // Double-check validation before processing
      const invalidMessage = getInvalidLicensesMessage(selectedBatchLicenses);
      if (invalidMessage) {
        setError(invalidMessage);
        return;
      }

      const licensesToActivate = selectedBatchLicenses.map(license => ({
        email: license.email,
        serialNo: license.serialNo,
        model: LICENSE_MODEL,
        isNew: license.isNew,
        dateEnd: license.dateEnd,
        dateStart: license.dateStart,
        durationInDay: licenseInput.durationInDay,
        durationDayCustom: licenseInput.durationDayCustom,
        type: licenseInput.type as LicenseENUM
      }));

      // Debug: Log the values being sent to API
      console.log('Batch Activate - License Input:', {
        durationInDay: licenseInput.durationInDay,
        durationDayCustom: licenseInput.durationDayCustom
      });
      console.log('Batch Activate - First License Data:', licensesToActivate[0]);

      const response = await licenseService.batchActivateLicenses(licensesToActivate);
      if (response.result) {
        setSuccessMessage('Licenses activated successfully');
        setBatchActivateDialog({ open: false, licenseInput: null });
        setSelectedBatchLicenses([]);
        // Add activated licenses to the set
        const newActivatedLicenses = new Set(activatedLicenses);
        selectedBatchLicenses.forEach(license => {
          newActivatedLicenses.add(`${license.email}-${license.serialNo}`);
        });
        setActivatedLicenses(newActivatedLicenses);

        // Refresh data from DB to get the latest information
        console.log('Refreshing batch license data after activation...');
        await handleBatchCheck();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError('Failed to activate licenses');
      console.error('Error activating licenses:', error);
    } finally {
      setBatchActivateLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Batch License Check Section */}

      <SectionCard
        title="Bulk activation"
        description="Paste serial numbers (one per line) and check them — every valid row is selected, then activate or extend them in one go."
      >
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2
        }}>
          <TextField
            label="Email (Optional)"
            value={batchEmail}
            onChange={(e) => setBatchEmail(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
          />
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2
        }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Enter Serial Numbers (one per line)"
            value={batchSerialNumbers}
            onChange={(e) => setBatchSerialNumbers(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={handleBatchCheck}
            disabled={batchLoading}
            sx={{
              minWidth: { xs: '100%', sm: 120 },
              height: { xs: 'auto', sm: '125px' }
            }}
          >
            {batchLoading ? <CircularProgress size={24} /> : 'Check'}
          </Button>
        </Box>

        {batchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {batchError}
          </Alert>
        )}

        {batchLicenses.length > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Found {batchLicenses.length} licenses
              </Typography>
              {selectedBatchLicenses.length > 0 && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleBatchActivateSelected}
                  disabled={batchLoading || batchActivateLoading}
                >
                  {batchActivateLoading ? 'Activating...' : `Activate Selected (${selectedBatchLicenses.length})`}
                </Button>
              )}
            </Box>

            <TableContainer component={Card}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedBatchLicenses.length === batchLicenses.length}
                        indeterminate={selectedBatchLicenses.length > 0 && selectedBatchLicenses.length < batchLicenses.length}
                        onChange={handleSelectAllBatchLicenses}
                      />
                    </TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Serial No</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Remaining Days</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batchLicenses.map((license, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        // Expired or brand-new entries are the ones an operator
                        // has to act on, so they carry a warning tint.
                        backgroundColor: (license.isNew || license.remainingDays === 0)
                          ? (theme) => tint(theme, 'warning.main', 0.12)
                          : 'inherit'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedBatchLicenses.some(l => l.email === license.email && l.serialNo === license.serialNo)}
                          onChange={() => handleSelectBatchLicense(license)}
                        />
                      </TableCell>
                      <TableCell>{license.email}</TableCell>
                      <TableCell>{license.serialNo}</TableCell>
                      <TableCell>{license.model}</TableCell>
                      <TableCell>{license.remainingDays}</TableCell>
                      <TableCell>
                        {license.dateEnd ? new Date(license.dateEnd).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{
                            backgroundColor: (license.isNew || license.remainingDays === 0) ? 'rgb(245, 241, 24)' : 'rgb(25, 118, 210)',
                            color: (license.isNew || license.remainingDays === 0) ? 'rgba(3, 3, 3, 0.81)' : 'rgb(255, 255, 255)'
                          }}
                          onClick={() => handleBatchActivate(license)}
                          disabled={activatedLicenses.has(`${license.email}-${license.serialNo}`)}
                        >
                          {license.isNew ? 'Activate' : 'Extend'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </SectionCard>

      {/* Batch License Dialog */}
      {batchDialogOpen && selectedBatchLicense && (
        <CreateLicenseDialog
          open={batchDialogOpen}
          onClose={() => {
            setBatchDialogOpen(false);
            setSelectedBatchLicense(null);
          }}
          email={selectedBatchLicense.email}
          serialNo={selectedBatchLicense.serialNo}
          type={selectedBatchLicense.type as LicenseENUM || LicenseENUM.BASIC}
          dateStart={selectedBatchLicense.dateStart}
          dateEnd={selectedBatchLicense.dateEnd}
          tool={LICENSE_MODEL}
          durationInDay={30}
          onSuccess={() => handleBatchCreateSuccess(selectedBatchLicense)}
        />
      )}

      {/* Batch Activate Dialog */}
      <BatchActivateDialog
        open={batchActivateDialog.open}
        onClose={() => setBatchActivateDialog({ open: false, licenseInput: null })}
        onConfirm={handleBatchActivateConfirm}
        selectedCount={selectedBatchLicenses.length}
        loading={batchActivateLoading}
      />

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LicenseRequestList;